import { createSlice, type PayloadAction, type ValidateSliceCaseReducers } from '@reduxjs/toolkit'
import { call, cancel, cancelled, fork, put, select, take } from 'redux-saga/effects'

type PageResult = { current: number | null; pageSize: number | null; total: number | null }

export interface ListData<T = any> {
  nextPage: number
  isLoading: boolean
  hasMore: boolean
  list: T[]
  page_result: PageResult
}

export interface ListState<T = any> {
  data: ListData<T>
}

type PatchPayload<T = any> = {
  isLoading: boolean
} & Partial<ListData<T>>

type ListReducers<SliceName extends string, T = any> = {
  [ActionName in `request${SliceName}Data`]: (state: ListState<T>) => void
} & {
  [ActionName in `clear${SliceName}Data`]: (state: ListState<T>) => void
} & {
  [ActionName in `patch${SliceName}Data`]: (state: ListState<T>, action: PayloadAction<Partial<ListData<T>>>) => void
}

export const createListSaga = <T = any>(
  sliceName: string,
  FETCH_LIST: string,
  RESET_LIST: string,
  patchDataAction: (payload: PatchPayload<T>) => PayloadAction<Partial<ListData<T>>>,
  clearDataAction: () => PayloadAction,
  fetchListApi: (data: { current: number }) => Promise<any>
) => {
  function* handleList() {
    let task: any
    while (true) {
      const data: any = yield take([FETCH_LIST, RESET_LIST])

      if (task && task.isRunning()) {
        if (data.type === RESET_LIST) {
          yield cancel(task)
          yield put(patchDataAction({ isLoading: false }))
        } else {
          // we are already running, abort the mission
          continue
        }
      }

      if (data.type === RESET_LIST) {
        yield put(clearDataAction())
      } else if (data.type === FETCH_LIST) {
        const oldData: ListData<T> = yield select(
          (state: any) => state[sliceName[0].toLowerCase() + sliceName.slice(1)].data
        )
        yield put(patchDataAction({ isLoading: true }))
        task = yield fork(fetchList, oldData)
      }
    }
  }

  function* fetchList(oldData: ListData<T>) {
    const page = oldData.nextPage
    const data = {
      current: page,
    }

    const response: any = yield call(fetchListApi, data)
    const newData = {
      nextPage: page + 1,
      isLoading: false,
      list: oldData?.list?.concat(response?.data),
      hasMore: response?.page_result?.total > response?.page_result?.current * response?.page_result?.pageSize,
      page_result: response.page_result,
    }

    if (!(yield cancelled())) {
      yield put(patchDataAction(newData))
    }
  }

  return handleList
}

export const createListSlice = <SliceName extends string, T = any>(sliceName: SliceName) => {
  const initialState: ListState<T> = {
    data: {
      nextPage: 1,
      isLoading: true,
      hasMore: true,
      list: [],

      page_result: { current: null, pageSize: null, total: null },
    },
  }

  const reducers = {
    [`request${sliceName}Data`]() {},
    [`clear${sliceName}Data`](state: ListState<T>) {
      state.data = {
        nextPage: 1,
        isLoading: true,
        hasMore: true,
        list: [],

        page_result: { current: null, pageSize: null, total: null },
      }
    },
    [`patch${sliceName}Data`](state: ListState<T>, action: PayloadAction<Partial<ListData<T>>>) {
      Object.assign(state.data, action.payload)
    },
  } as ValidateSliceCaseReducers<ListState<T>, ListReducers<SliceName, T>>

  const slice = createSlice<ListState<T>, ListReducers<SliceName, T>>({
    name: sliceName[0].toLowerCase() + sliceName.slice(1),
    initialState,
    reducers,
  })

  return slice
}

export const createListSelector =
  <T = any>(sliceName: string) =>
  (state: any): ListData<T> =>
    state[sliceName[0].toLowerCase() + sliceName.slice(1)].data
